// frontend/src/components/profile/ResidentialAddressEditor.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Building, Globe, Info, Lightbulb, MapPin } from 'lucide-react';
import PostalCodeLookup from '../UI/PostalCodeLookup';
import PillToggle from '../UI/PillToggle';
import { InfoBadge } from '../UI/InfoBadge';
import { SensitiveFieldDisplay as DefaultViewField } from '../UI/SensitiveFieldDisplay';
import toast from '../../utils/toast';
import {
  OVERSEAS_RESIDENTIAL_COUNTRY,
  ensureCountryInAddress,
  getOverseasAddressFieldCopy,
  inferResidentialAddressLocation,
  isWorkPassResidentialAddressDigestEligible,
} from '../../utils/residentialAddressUtils';

export const DEFAULT_RESIDENTIAL_FIELD_KEYS = {
  address: 'hrprofile_residential_address',
  postal: 'hrprofile_residential_postal_code',
  unit: 'hrprofile_residential_unit_number',
  location: 'hrprofile_residential_address_location',
  country: 'hrprofile_residential_address_country',
};

/**
 * Shared Singapore / Overseas residential address editor.
 *
 * Mental model:
 * 1. PillToggle — Singapore or Overseas
 * 2. Singapore → Postal Code Lookup → Residential Address / Unit
 * 3. Overseas → Malaysia only (commute) → Residential Address / Unit
 *
 * @param {'self'|'hr'} audience  Copy voice: employee self-service vs HR admin
 * @param {object} [fieldKeys]    Override form field names (Add Employee uses short keys)
 */
export default function ResidentialAddressEditor({
  isEditing,
  formData = {},
  formErrors = {},
  setFormData,
  setFormErrors,
  onFieldChange,
  onChangeField,
  SensitiveFieldDisplay,
  changedFields = {},
  sensitiveFieldsVisible = {},
  toggleFieldVisibility = () => {},
  primaryAddress = null,
  theme = 'employee',
  audience = 'self',
  fieldKeys: fieldKeysProp,
  showAddressTypeBadge = true,
  employmentPassType = '',
}) {
  const isAdmin = theme === 'admin';
  const isHr = audience === 'hr' || isAdmin;
  const accentIcon = isAdmin ? 'text-red-600' : 'text-blue-800';
  const labelClass = isAdmin ? 'form-label-admin' : 'form-label';
  const inputClass = isAdmin ? 'input-admin' : 'input px-4 rounded-full';
  // DS: address display uses .field-display-multiline (min-h-[60px]). Match that
  // height for address edit instead of default .textarea min-h-[120px].
  const addressTextareaClass = isAdmin
    ? 'textarea-admin min-h-[60px]'
    : 'textarea min-h-[60px]';
  const wasEditingRef = useRef(false);
  const keys = useMemo(
    () => ({ ...DEFAULT_RESIDENTIAL_FIELD_KEYS, ...(fieldKeysProp || {}) }),
    [fieldKeysProp]
  );

  const addressVal = formData[keys.address] || '';
  const postalVal = formData[keys.postal] || '';
  const unitVal = formData[keys.unit] || '';
  const locationVal = formData[keys.location] || '';
  const countryValRaw = formData[keys.country] || '';

  const inferredLocation = useMemo(
    () =>
      inferResidentialAddressLocation({
        address: addressVal,
        postalCode: postalVal,
        location: locationVal,
        country: countryValRaw,
      }),
    [addressVal, postalVal, locationVal, countryValRaw]
  );

  const [locationMode, setLocationMode] = useState(inferredLocation);

  useEffect(() => {
    const justEnteredEdit = isEditing && !wasEditingRef.current;
    const justLeftEdit = !isEditing && wasEditingRef.current;
    if (justEnteredEdit || justLeftEdit || !isEditing) {
      setLocationMode(inferredLocation);
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, inferredLocation]);

  const overseasCopy = getOverseasAddressFieldCopy();

  const patchForm = (patch, errorPatch = null) => {
    if (typeof setFormData === 'function') {
      setFormData((prev) => ({ ...prev, ...patch }));
    } else if (typeof onChangeField === 'function') {
      Object.entries(patch).forEach(([name, value]) => onChangeField(name, value));
    }
    if (errorPatch && setFormErrors) {
      setFormErrors((prev) => ({ ...prev, ...errorPatch }));
    }
  };

  const emitFieldChange = (name, value) => {
    if (typeof onChangeField === 'function') {
      onChangeField(name, value);
      return;
    }
    if (typeof onFieldChange === 'function') {
      onFieldChange({ target: { name, value } });
    }
  };

  const handleNativeChange = (e) => {
    const { name, value } = e.target;
    emitFieldChange(name, value);
  };

  const handleLocationChange = (nextMode) => {
    if (nextMode === locationMode) return;
    setLocationMode(nextMode);

    if (nextMode === 'overseas') {
      const postal = String(postalVal || '').trim();
      patchForm({
        [keys.location]: 'overseas',
        [keys.country]: OVERSEAS_RESIDENTIAL_COUNTRY,
        [keys.postal]: /^\d{6}$/.test(postal) ? '' : postal,
      });
      return;
    }

    patchForm({
      [keys.location]: 'singapore',
      [keys.country]: '',
    });
  };

  // Overseas is Malaysia-only — keep country locked while editing
  useEffect(() => {
    if (!isEditing || locationMode !== 'overseas') return;
    if (countryValRaw === OVERSEAS_RESIDENTIAL_COUNTRY) return;
    patchForm({
      [keys.location]: 'overseas',
      [keys.country]: OVERSEAS_RESIDENTIAL_COUNTRY,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync country lock only
  }, [isEditing, locationMode, countryValRaw, keys.country, keys.location]);

  const handleOverseasAddressBlur = () => {
    const address = formData[keys.address];
    if (!address) return;
    const withCountry = ensureCountryInAddress(address, OVERSEAS_RESIDENTIAL_COUNTRY);
    if (withCountry !== address) {
      patchForm({ [keys.address]: withCountry });
    }
  };

  const displayAddress = isEditing
    ? addressVal
    : primaryAddress?.address || addressVal || '';
  const displayUnit = isEditing
    ? unitVal
    : primaryAddress?.unit_number || unitVal || '';
  const displayLabel = primaryAddress?.display_label || 'Residential Address';
  const showOverseasBadge = !isEditing && inferredLocation === 'overseas';

  const locationQuestion = isHr
    ? "Where is this employee's residential address?"
    : 'Where is your residential address?';
  const locationHelp = isHr
    ? 'Singapore uses postal lookup. Overseas is Malaysia only (e.g. Johor commute).'
    : 'Singapore uses postal lookup. Overseas means Malaysia only (e.g. Johor commute).';

  const FieldComponent = SensitiveFieldDisplay || null;
  const changedHighlight = (fieldKey) =>
    changedFields?.[fieldKey]
      ? isAdmin
        ? 'border-red-300 bg-red-50/30'
        : 'border-blue-300 bg-blue-50/30'
      : '';

  const renderSingaporeAddressFields = () => {
    if (FieldComponent) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div className="md:col-span-2">
            <FieldComponent
              label={displayLabel}
              name={keys.address}
              value={displayAddress}
              fieldName="residential_address"
              type="address"
              isEditing={isEditing}
              onChange={onFieldChange || handleNativeChange}
              error={formErrors[keys.address]}
              icon={<MapPin className="w-4 h-4 text-purple-500" />}
              placeholder="Enter residential address"
              hasChanged={changedFields?.[keys.address] || false}
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
              // Profile SensitiveFieldDisplay: isTextArea → field-display-multiline in view
              isTextArea
              helpText={
                isEditing
                  ? 'Auto-filled from postal code — you can fine-tune if needed'
                  : undefined
              }
            />
          </div>
          <div>
            <FieldComponent
              label="Unit Number"
              name={keys.unit}
              value={displayUnit}
              fieldName="residential_unit_number"
              type="default"
              isEditing={isEditing}
              onChange={onFieldChange || handleNativeChange}
              error={formErrors[keys.unit]}
              icon={<Building className="w-4 h-4 text-purple-500" />}
              placeholder="e.g., #12-34"
              hasChanged={changedFields?.[keys.unit] || false}
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
            />
          </div>
        </div>
      );
    }

    if (isEditing) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div className="md:col-span-2">
            <label className={`${labelClass} flex items-center gap-2`}>
              <MapPin className="w-4 h-4 text-purple-500" />
              {displayLabel}
            </label>
            <textarea
              name={keys.address}
              value={addressVal}
              onChange={handleNativeChange}
              rows={2}
              className={`${addressTextareaClass} ${changedHighlight(keys.address)}`}
              placeholder="Enter residential address"
            />
            <p className="form-help">Auto-filled from postal code — you can fine-tune if needed</p>
          </div>
          <div>
            <label className={`${labelClass} flex items-center gap-2`}>
              <Building className="w-4 h-4 text-purple-500" />
              Unit Number
            </label>
            <input
              type="text"
              name={keys.unit}
              value={unitVal}
              onChange={handleNativeChange}
              className={`${inputClass} ${changedHighlight(keys.unit)}`}
              placeholder="e.g., #12-34"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        <div className="md:col-span-2">
          <DefaultViewField
            label={displayLabel}
            value={displayAddress}
            fieldName="residential_address"
            type="address"
            multiline
            icon={<MapPin className="w-4 h-4 text-purple-500" />}
          />
        </div>
        <div>
          <DefaultViewField
            label="Unit Number"
            value={displayUnit}
            fieldName="residential_unit_number"
            type="default"
            icon={<Building className="w-4 h-4 text-purple-500" />}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {isEditing && (
        <>
          {!isHr && isWorkPassResidentialAddressDigestEligible(employmentPassType) && (
            <div
              className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3"
              role="note"
            >
              <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-blue-800">
                If you change your residential address, HR will see it in the next morning digest. They will open your employee record for the new address.
              </p>
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <label className={`${labelClass} mb-0.5 flex items-center gap-2`}>
                <Globe className={`w-4 h-4 ${accentIcon}`} />
                {locationQuestion}
              </label>
              <p className="form-help mb-0">{locationHelp}</p>
            </div>
            <PillToggle
              value={locationMode}
              onChange={handleLocationChange}
              options={[
                { id: 'singapore', label: 'Singapore', icon: MapPin },
                { id: 'overseas', label: 'Overseas', icon: Globe },
              ]}
              variant="text"
              theme={isAdmin ? 'admin' : 'employee'}
              layout="sectionHeader"
              ariaLabel="Residential address location"
            />
          </div>

          {locationMode === 'singapore' ? (
            <div className="space-y-3">
              <PostalCodeLookup
                value={postalVal}
                onAddressFound={(geocodedData) => {
                  patchForm(
                    {
                      [keys.address]: geocodedData.address,
                      [keys.postal]: geocodedData.postal_code || '',
                      [keys.location]: 'singapore',
                      [keys.country]: '',
                    },
                    {
                      [keys.address]: '',
                      [keys.postal]: '',
                    }
                  );
                  toast.success('Address auto-filled from postal code');
                }}
                onPostalCodeChange={(newPostalCode) => {
                  patchForm({
                    [keys.postal]: newPostalCode,
                    [keys.location]: 'singapore',
                  });
                }}
                onError={(error) => toast.error(error)}
                placeholder="Enter postal code to auto-fill address (e.g., 018989)"
                buttonText="Find Address"
                isAdmin={isAdmin}
              />
              <p className="form-help flex items-start gap-1.5">
                <Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>Enter a 6-digit Singapore postal code to auto-fill the residential address.</span>
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className={`${labelClass} flex items-center gap-2`}>
                  <Globe className={`w-4 h-4 ${accentIcon}`} />
                  Country
                </label>
                <p className="field-static">{OVERSEAS_RESIDENTIAL_COUNTRY}</p>
                <p className="form-help">
                  Overseas residential addresses are Malaysia only — staff who commute across the
                  border to work in Singapore.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`${labelClass} flex items-center gap-2`}>
                    <MapPin className="w-4 h-4 text-purple-500" />
                    Residential Address
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name={keys.address}
                    value={addressVal}
                    onChange={handleNativeChange}
                    onBlur={handleOverseasAddressBlur}
                    rows={2}
                    className={`${addressTextareaClass} ${
                      formErrors[keys.address] ? 'input-error' : ''
                    } ${changedHighlight(keys.address)}`}
                    placeholder={overseasCopy.addressPlaceholder}
                  />
                  {formErrors[keys.address] ? (
                    <p className="form-error">{formErrors[keys.address]}</p>
                  ) : (
                    <p className="form-help">{overseasCopy.addressHelp}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>{overseasCopy.postalLabel}</label>
                    <input
                      type="text"
                      name={keys.postal}
                      value={postalVal}
                      onChange={handleNativeChange}
                      maxLength={20}
                      className={`${inputClass} ${changedHighlight(keys.postal)}`}
                      placeholder={overseasCopy.postalPlaceholder}
                    />
                    <p className="form-help">{overseasCopy.postalHelp}</p>
                  </div>
                  <div>
                    <label className={`${labelClass} flex items-center gap-2`}>
                      <Building className="w-4 h-4 text-purple-500" />
                      {overseasCopy.unitLabel}
                    </label>
                    <input
                      type="text"
                      name={keys.unit}
                      value={unitVal}
                      onChange={handleNativeChange}
                      className={`${inputClass} ${changedHighlight(keys.unit)}`}
                      placeholder={overseasCopy.unitPlaceholder}
                    />
                    <p className="form-help">{overseasCopy.unitHelp}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {(!isEditing || locationMode === 'singapore') && renderSingaporeAddressFields()}

      <div className="flex flex-wrap items-center gap-2">
        {showAddressTypeBadge && primaryAddress?.address_type && (
          <InfoBadge
            color={primaryAddress.address_type === 'dormitory' ? 'orange' : 'blue'}
            label={
              primaryAddress.address_type === 'dormitory'
                ? 'Dormitory Address'
                : 'Residential Address'
            }
          />
        )}
        {showOverseasBadge && (
          <InfoBadge
            color="purple"
            label={`Overseas · ${OVERSEAS_RESIDENTIAL_COUNTRY}`}
          />
        )}
      </div>
    </div>
  );
}
